import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext, Field } from 'formik';
import {
  FormTextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import InformationOverlay from '../../../../../components/InformationOverlay';

import './style.scss';

/**
 * Display modes for PersonalInformationSection
 * - 'form': Shows editable form fields (default)
 * - 'readonly': Shows data as plain text (for free products in V2)
 */
type DisplayMode = 'form' | 'readonly';

/**
 * Form values interface for Formik context
 */
interface PersonalInfoFormValues {
  personal_given_name: string;
  personal_family_name: string;
  email: string;
  telephone: string;
}

/**
 * Personal Information Section Component
 *
 * Renders form fields or read-only display for customer personal information.
 * Supports two display modes:
 * - Form mode: Editable fields for first name, last name, email, telephone
 * - Readonly mode: Static text display for free product checkouts
 *
 * @component
 */
interface PersonalInformationSectionProps {
  /** Customer email address - used to disable email field for logged-in users */
  customerEmail: string | null;
  /**
   * Display mode for the section
   * - 'form': Shows editable form fields (default)
   * - 'readonly': Shows data as plain text (for free products)
   * @default 'form'
   */
  displayMode?: DisplayMode;
  /**
   * Whether to disable first and last name fields (V2 checkout only)
   * @default false
   */
  disableNameFields?: boolean;
}

const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({
  customerEmail,
  displayMode = 'form',
  disableNameFields = false,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<PersonalInfoFormValues>();

  /**
   * Renders the section in readonly mode showing user data as plain text.
   * Hidden fields ensure Formik properly tracks values for form submission.
   */
  const renderReadonlyMode = (): React.ReactElement => (
    <div className="PersonalInformationSection__readonly">
      {/* Hidden fields to ensure Formik tracks values for submission */}
      <Field type="hidden" name="personal_given_name" />
      <Field type="hidden" name="personal_family_name" />
      <Field type="hidden" name="email" />
      <div className="PersonalInformationSection__readonly-row">
        <p>
          {values.personal_given_name || '-'} {values.personal_family_name || '-'}
        </p>
        <p>
          {values.email || '-'}
        </p>
      </div>
    </div>
  );

  /**
   * Renders the section in form mode with editable fields
   */
  const renderFormMode = (): React.ReactElement => (
    <div className="row">
      <div className="col-12 col-md-6">
        <FormTextField
          title={t('checkout.first_name.label')}
          fieldName="personal_given_name"
          disabled={disableNameFields}
        />
      </div>
      <div className="col-12 col-md-6">
        <FormTextField
          title={t('checkout.last_name.label')}
          fieldName="personal_family_name"
          disabled={disableNameFields}
        />
      </div>
      <div className="col-12 col-md-6">
        <FormTextField
          title={t('checkout.email.label')}
          fieldName="email"
          disabled={customerEmail !== null}
        />
      </div>
      <div className="col-12 col-md-6">
        <FormTextField
          title={t('checkout.telephone.label')}
          fieldName="telephone"
        />
      </div>
    </div>
  );

  return (
    <fieldset className="PersonalInformationSection">
      <legend className="PersonalInformationSection__legend">
        {t('checkout.personal_info')}
        <InformationOverlay
          overlayKey="FieldsetOverlay__personal_info"
          title={t('checkout.personal_info_overlay_title')}
          body={t('checkout.personal_info_overlay_body')}
        />
      </legend>

      {displayMode === 'readonly' ? renderReadonlyMode() : renderFormMode()}
    </fieldset>
  );
};

export default PersonalInformationSection;
